import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import FloatingNavigation from "../components/FloatingNavigation";
import {
  FaEdit,
  FaTrash,
  FaUsers,
  FaUserShield,
  FaUserTie,
  FaUserCog,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaEnvelope,
  FaMapMarkerAlt,
  FaSearch,
  FaFilter,
  FaDownload,
  FaTimes,
  FaUserPlus
} from "react-icons/fa";

export default function UserManage() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "Agent",
    assignedSite: "",
    assignedSites: [],
  });
  const [editingUserId, setEditingUserId] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUsers();
      fetchSites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err.message);
    }
  };

  const fetchSites = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sites/active`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSites(res.data.sites || []);
    } catch (err) {
      console.error("Error fetching sites:", err.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const generatePassword = () => {
    const password = Math.random().toString(36).slice(-8);
    setFormData((prev) => ({ ...prev, password }));
    navigator.clipboard.writeText(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let { name, email, password, role } = formData;

      if (!name) {
        alert("Please fill all required fields");
        return;
      }

      // Validate site assignment based on role
      if (role === "Team" && !formData.assignedSite) {
        alert("Please select a site for Team users");
        return;
      }

      if (role === "Agent" && formData.assignedSites.length === 0) {
        alert("Please select at least one site for Agent users");
        return;
      }

      if (role === "Agent") {
        const timestamp = Date.now();
        email = `${timestamp}@vr-archives.com`;
        password = "dummypwd@64";
      } else {
        if (!email || !password) {
          alert("Please fill all required fields");
          return;
        }
      }

      const dataToSend = { name, email, password, role };

      // Add site assignment based on role
      if (role === "Team" && formData.assignedSite) {
        dataToSend.assignedSite = formData.assignedSite;
      } else if (role === "Agent" && formData.assignedSites.length > 0) {
        dataToSend.assignedSites = formData.assignedSites;
      }

      if (editingUserId) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/${editingUserId}`,
          dataToSend,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        alert(`✅ User "${name}" has been updated successfully.`);
        setEditingUserId(null);
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/users`,
          dataToSend,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        alert(`✅ User "${name}" has been created successfully.`);
      }

      setFormData({
        name: "",
        email: "",
        password: "",
        role: "Agent",
        assignedSite: "",
        assignedSites: [],
      });
      setShowAddForm(false);
      fetchUsers();
    } catch (err) {
      console.error("Error saving/updating user:", err);
      alert(`❌ Error ${editingUserId ? 'updating' : 'creating'} user: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleEdit = (user) => {
    // Protect system admin account
    if (user.email === "admin@pos.com") {
      alert("⚠️ System Admin account cannot be edited for security reasons.");
      return;
    }

    setFormData({
      name: user.name,
      email: user.email || "",
      password: "",
      role: user.role,
      assignedSite: user.assignedSite?._id || "",
      assignedSites: user.assignedSites?.map((site) => site._id) || [],
    });
    setEditingUserId(user._id);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    // Find the user to check if it's system admin
    const userToDelete = users.find(user => user._id === id);
    
    // Protect system admin account
    if (userToDelete && userToDelete.email === "admin@pos.com") {
      alert("⚠️ System Admin account cannot be deleted for security reasons.");
      return;
    }

    // Confirmation dialog
    const isConfirmed = window.confirm(
      `Are you sure you want to delete user "${userToDelete?.name}"?\n\nThis action cannot be undone.`
    );
    
    if (!isConfirmed) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      // Show success message
      alert(`✅ User "${userToDelete?.name}" has been deleted successfully.`);
      
      // Refresh users list
      fetchUsers();
      
      // If we were editing this user, reset the form
      if (editingUserId === id) {
        resetForm();
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      alert(`❌ Error deleting user: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleSiteCheckboxChange = (siteId) => {
    setFormData((prev) => ({
      ...prev,
      assignedSites: prev.assignedSites.includes(siteId)
        ? prev.assignedSites.filter((id) => id !== siteId)
        : [...prev.assignedSites, siteId],
    }));
  };

  const filteredUsers = users.filter((user) => {
    // Handle search term safely
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (user.name && user.name.toLowerCase().includes(searchLower)) ||
      (user.email && user.email.toLowerCase().includes(searchLower));
    
    const matchesRole = roleFilter === "all" || user.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  const exportToCSV = () => {
    const csvData = users.map(user => ({
      "Name": user.name,
      "Email": user.email || "N/A",
      "Role": user.role,
      "Assigned Site": user.assignedSite ? (user.assignedSite.displayName || user.assignedSite.siteName) : "N/A",
      "Created": new Date(user.createdAt).toLocaleDateString()
    }));

    const csvContent = [
      Object.keys(csvData[0]).join(","),
      ...csvData.map(row => Object.values(row).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "Agent",
      assignedSite: "",
      assignedSites: [],
    });
    setEditingUserId(null);
    setShowAddForm(false);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case "Admin": return <FaUserShield className="text-red-500" />;
      case "Team": return <FaUserTie className="text-blue-500" />;
      case "Agent": return <FaUserCog className="text-green-500" />;
      default: return <FaUsers className="text-gray-500" />;
    }
  };

  const getRoleStats = () => {
    return {
      total: users.length,
      admin: users.filter(u => u.role === "Admin").length,
      team: users.filter(u => u.role === "Team").length,
      agent: users.filter(u => u.role === "Agent").length,
    };
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage user accounts, roles, and site assignments
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaDownload className="mr-2" />
                Export
              </button>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaUserPlus className="mr-2" />
                {showAddForm ? "Cancel" : "Add User"}
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-gray-100">
                <FaUsers className="h-6 w-6 text-gray-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Users</p>
                <p className="text-2xl font-semibold text-gray-900">{getRoleStats().total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-red-100">
                <FaUserShield className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Admins</p>
                <p className="text-2xl font-semibold text-gray-900">{getRoleStats().admin}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100">
                <FaUserTie className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Team Members</p>
                <p className="text-2xl font-semibold text-gray-900">{getRoleStats().team}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-green-100">
                <FaUserCog className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Agents</p>
                <p className="text-2xl font-semibold text-gray-900">{getRoleStats().agent}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit User Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingUserId ? "Edit User" : "Add New User"}
              </h3>
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="Agent">Agent</option>
                    <option value="Team">Team</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter full name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              {/* Email and Password for Admin/Team */}
              {(formData.role === "Admin" || formData.role === "Team") && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <div className="relative">
                      <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        placeholder="Enter email address"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="flex space-x-2">
                      <div className="relative flex-1">
                        <FaKey className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="Enter password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                          className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={generatePassword}
                        className="px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Generate
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Site Assignment for Team */}
              {formData.role === "Team" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Assigned Site
                  </label>
                  <select
                    name="assignedSite"
                    value={formData.assignedSite}
                    onChange={handleInputChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    required
                  >
                    <option value="">Select Site</option>
                    {sites.map((site) => (
                      <option key={site._id} value={site._id}>
                        {site.displayName || site.siteName}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Multiple Site Assignment for Agent */}
              {formData.role === "Agent" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Assign to Sites
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {sites.map((site) => (
                      <label
                        key={site._id}
                        className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.assignedSites.includes(site._id)}
                          onChange={() => handleSiteCheckboxChange(site._id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {site.displayName || site.siteName}
                          </p>
                          <p className="text-xs text-gray-500">ID: {site.siteName}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {editingUserId ? "Update User" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Users
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="all">All Roles</option>
                <option value="Admin">Admin</option>
                <option value="Team">Team</option>
                <option value="Agent">Agent</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm("");
                  setRoleFilter("all");
                }}
                className="w-full px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaFilter className="inline mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Users List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Users ({filteredUsers.length})
            </h3>
          </div>
          <div className="p-6">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <FaUsers className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm || roleFilter !== "all" 
                    ? "Try adjusting your search or filters" 
                    : "Get started by creating your first user"}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {["Admin", "Team", "Agent"].map((role) => {
                  const roleUsers = filteredUsers.filter(u => u.role === role);
                  if (roleUsers.length === 0) return null;
                  
                  return (
                    <div key={role}>
                      <div className="flex items-center mb-4">
                        {getRoleIcon(role)}
                        <h4 className="ml-2 text-md font-semibold text-gray-900">
                          {role}s ({roleUsers.length})
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        {roleUsers.map((user) => (
                          <div
                            key={user._id}
                            className={`border rounded-lg p-4 hover:shadow-md transition-shadow duration-200 ${
                              user.email === "admin@pos.com" 
                                ? "border-yellow-300 bg-yellow-50" 
                                : "border-gray-200"
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                                    user.email === "admin@pos.com"
                                      ? "bg-gradient-to-br from-yellow-500 to-orange-600"
                                      : "bg-gradient-to-br from-blue-500 to-blue-700"
                                  }`}>
                                    {user.email === "admin@pos.com" ? "🛡️" : user.name?.charAt(0)?.toUpperCase()}
                                  </div>
                                  <div>
                                    <div className="flex items-center space-x-2">
                                      <h5 className="text-lg font-semibold text-gray-900">
                                        {user.name}
                                      </h5>
                                      {user.email === "admin@pos.com" && (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                          🛡️ System Admin
                                        </span>
                                      )}
                                    </div>
                                    {user.email && (
                                      <p className="text-sm text-gray-500">{user.email}</p>
                                    )}
                                  </div>
                                  <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                      user.role === "Admin"
                                        ? "bg-red-100 text-red-800"
                                        : user.role === "Team"
                                        ? "bg-blue-100 text-blue-800"
                                        : "bg-green-100 text-green-800"
                                    }`}
                                  >
                                    {user.role}
                                  </span>
                                </div>
                                
                                {/* Site Information */}
                                {user.role === "Team" && user.assignedSite && (
                                  <div className="mt-3 flex items-center text-sm text-gray-500">
                                    <FaMapMarkerAlt className="mr-2" />
                                    <span>
                                      Site: {user.assignedSite.displayName || user.assignedSite.siteName}
                                    </span>
                                  </div>
                                )}
                                
                                {user.role === "Agent" && user.assignedSites && user.assignedSites.length > 0 && (
                                  <div className="mt-3">
                                    <div className="flex items-center text-sm text-gray-500 mb-2">
                                      <FaMapMarkerAlt className="mr-2" />
                                      <span>Sites ({user.assignedSites.length}):</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                      {user.assignedSites.map((site, index) => (
                                        <span
                                          key={index}
                                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                                        >
                                          {site.displayName || site.siteName}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                {user.email === "admin@pos.com" ? (
                                  <div className="flex items-center space-x-2 text-xs text-yellow-600">
                                    <span className="bg-yellow-100 px-2 py-1 rounded">Protected Account</span>
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleEdit(user)}
                                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                                      title="Edit User"
                                    >
                                      <FaEdit />
                                    </button>
                                    <button
                                      onClick={() => handleDelete(user._id)}
                                      className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                                      title="Delete User"
                                    >
                                      <FaTrash />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      <FloatingNavigation />
    </div>
  );
}
